/**
 * XML Parts service — persists import metadata in Excel Custom XML Parts.
 * Data survives save/close/reopen of the workbook.
 * Schema follows PRD §10.1.
 *
 * Uses the Excel JavaScript API (Excel.run / context.workbook.customXmlParts),
 * NOT the Common API (Office.context.document.customXmlParts) which is Word-only.
 */

import type { ImportCardData, DataSourceType } from "../types/imports";

const NAMESPACE = "urn:cashboard:import";
const LOCK_NAMESPACE = "urn:cashboard:lock";
const LOCK_STALE_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Save import metadata as a Custom XML Part.
 * Returns the xmlPartId for later retrieval/deletion.
 */
export async function saveImportMetadata(
  card: ImportCardData,
): Promise<string> {
  const xml = buildImportXml(card);
  return Excel.run(async (context) => {
    const xmlPart = context.workbook.customXmlParts.add(xml);
    xmlPart.load("id");
    await context.sync();
    return xmlPart.id;
  });
}

export interface ReadAllResult {
  cards: ImportCardData[];
  skippedCount: number;
}

/**
 * Read all Cashboard import metadata from Custom XML Parts.
 * Returns ImportCardData[] with status "imported" (restored state),
 * plus a count of parts that were skipped due to parse errors.
 */
export async function readAllImportMetadata(): Promise<ReadAllResult> {
  try {
    return await Excel.run(async (context) => {
      const xmlParts = context.workbook.customXmlParts.getByNamespace(NAMESPACE);
      xmlParts.load("items");
      await context.sync();

      if (xmlParts.items.length === 0) {
        return { cards: [], skippedCount: 0 };
      }

      // Load XML content from each part
      const xmlResults = xmlParts.items.map((part) => ({
        id: part.id,
        xml: part.getXml(),
      }));
      await context.sync();

      const cards: ImportCardData[] = [];
      let skippedCount = 0;
      for (const item of xmlResults) {
        try {
          const card = parseImportXml(item.xml.value, item.id);
          cards.push(card);
        } catch {
          skippedCount++;
        }
      }
      return { cards, skippedCount };
    });
  } catch {
    // Custom XML Parts API may not be available or may be in a bad state
    return { cards: [], skippedCount: 0 };
  }
}

/**
 * Delete a single import's metadata by its XML Part ID.
 */
export async function deleteImportMetadata(
  xmlPartId: string,
): Promise<void> {
  return Excel.run(async (context) => {
    const xmlPart = context.workbook.customXmlParts.getItem(xmlPartId);
    xmlPart.delete();
    await context.sync();
  });
}

/**
 * Update an existing import's metadata (delete old + save new).
 * Returns the new xmlPartId.
 */
export async function updateImportMetadata(
  card: ImportCardData,
): Promise<string> {
  if (card.xmlPartId) {
    try {
      await deleteImportMetadata(card.xmlPartId);
    } catch {
      // Best-effort deletion
    }
  }
  return saveImportMetadata(card);
}

// ---- Co-authoring refresh lock ----

/**
 * Set a refresh lock in Custom XML Parts.
 * Prevents concurrent refresh in co-authoring sessions.
 */
export async function setRefreshLock(): Promise<void> {
  try {
    // Clear any existing lock first
    await clearRefreshLock();
    const xml = [
      `<?xml version="1.0" encoding="UTF-8"?>`,
      `<CashboardRefreshLock xmlns="${LOCK_NAMESPACE}">`,
      `  <Timestamp>${new Date().toISOString()}</Timestamp>`,
      `</CashboardRefreshLock>`,
    ].join("\n");
    await Excel.run(async (context) => {
      context.workbook.customXmlParts.add(xml);
      await context.sync();
    });
  } catch {
    // Best-effort — don't block refresh if lock can't be set
  }
}

/**
 * Clear the refresh lock.
 */
export async function clearRefreshLock(): Promise<void> {
  try {
    await Excel.run(async (context) => {
      const parts = context.workbook.customXmlParts.getByNamespace(LOCK_NAMESPACE);
      parts.load("items");
      await context.sync();
      for (const part of parts.items) {
        part.delete();
      }
      await context.sync();
    });
  } catch {
    // Best-effort
  }
}

/**
 * Check if a refresh lock is currently held (and not stale).
 * Returns true if another user is actively refreshing.
 */
export async function checkRefreshLock(): Promise<boolean> {
  try {
    return await Excel.run(async (context) => {
      const parts = context.workbook.customXmlParts.getByNamespace(LOCK_NAMESPACE);
      parts.load("items");
      await context.sync();

      if (parts.items.length === 0) return false;

      const xmlResult = parts.items[0].getXml();
      await context.sync();

      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlResult.value, "text/xml");
      const timestamp = doc.getElementsByTagName("Timestamp")[0]?.textContent;
      if (!timestamp) return false;

      const lockAge = Date.now() - new Date(timestamp).getTime();
      return lockAge < LOCK_STALE_MS;
    });
  } catch {
    return false;
  }
}

// ---- Internal helpers ----

function buildImportXml(card: ImportCardData): string {
  const lines = [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<CashboardImport xmlns="${NAMESPACE}">`,
    `  <ImportId>${escapeXml(card.id)}</ImportId>`,
    `  <MetabaseCardId>${escapeXml(card.dataSource.id)}</MetabaseCardId>`,
    `  <DisplayName>${escapeXml(card.dataSource.name)}</DisplayName>`,
    `  <DataSourceType>${escapeXml(card.dataSource.type)}</DataSourceType>`,
    `  <Collection>${escapeXml(card.dataSource.collection)}</Collection>`,
    `  <SheetId>${escapeXml(card.sheetId || "")}</SheetId>`,
    `  <SheetName>${escapeXml(card.sheetName || "")}</SheetName>`,
    `  <TableName>${escapeXml(card.tableName || "")}</TableName>`,
    `  <LastRefreshed>${escapeXml(card.lastRefreshed || new Date().toISOString())}</LastRefreshed>`,
    `  <RowCount>${card.rowCount || 0}</RowCount>`,
    `  <ContentHash>${escapeXml(card.contentHash || "")}</ContentHash>`,
  ];

  // Column schema snapshot
  if (card.columnSchema && card.columnSchema.length > 0) {
    lines.push(`  <ColumnSchema>`);
    card.columnSchema.forEach((col, i) => {
      lines.push(`    <Column name="${escapeXml(col)}" order="${i}" />`);
    });
    lines.push(`  </ColumnSchema>`);
  }

  // Date filter (only if configured)
  if (card.dateColumn && card.datePreset) {
    lines.push(`  <DateFilter>`);
    lines.push(`    <Column>${escapeXml(card.dateColumn)}</Column>`);
    lines.push(`    <ColumnDisplayName>${escapeXml(card.dateColumnDisplayName || card.dateColumn)}</ColumnDisplayName>`);
    lines.push(`    <Preset>${escapeXml(card.datePreset)}</Preset>`);
    if (card.afterMonth) {
      lines.push(`    <AfterMonth>${escapeXml(card.afterMonth)}</AfterMonth>`);
    }
    lines.push(`  </DateFilter>`);
  }

  lines.push(`</CashboardImport>`);
  return lines.join("\n");
}

function parseImportXml(xml: string, xmlPartId: string): ImportCardData {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, "text/xml");

  // Check for XML parse errors
  const parseError = doc.getElementsByTagName("parsererror");
  if (parseError.length > 0) {
    throw new Error("Malformed XML");
  }

  const get = (tag: string): string =>
    doc.getElementsByTagName(tag)[0]?.textContent || "";

  // Validate required fields
  const importId = get("ImportId");
  const cardId = get("MetabaseCardId");
  const sheetId = get("SheetId");
  if (!importId || !cardId || !sheetId) {
    throw new Error("Missing required fields in XML Part");
  }

  // Parse column schema
  const schemaEl = doc.getElementsByTagName("ColumnSchema")[0];
  let columnSchema: string[] | undefined;
  if (schemaEl) {
    const colEls = schemaEl.getElementsByTagName("Column");
    const cols: { name: string; order: number }[] = [];
    for (let i = 0; i < colEls.length; i++) {
      cols.push({
        name: colEls[i].getAttribute("name") || "",
        order: parseInt(colEls[i].getAttribute("order") || "0", 10),
      });
    }
    cols.sort((a, b) => a.order - b.order);
    columnSchema = cols.map((c) => c.name);
  }

  // Parse date filter (nested inside <DateFilter>)
  const dateFilterEl = doc.getElementsByTagName("DateFilter")[0];
  let dateColumn: string | undefined;
  let dateColumnDisplayName: string | undefined;
  let datePreset: string | undefined;
  let afterMonth: string | undefined;
  if (dateFilterEl) {
    dateColumn =
      dateFilterEl.getElementsByTagName("Column")[0]?.textContent || undefined;
    dateColumnDisplayName =
      dateFilterEl.getElementsByTagName("ColumnDisplayName")[0]?.textContent ||
      dateColumn;
    datePreset =
      dateFilterEl.getElementsByTagName("Preset")[0]?.textContent || undefined;
    afterMonth =
      dateFilterEl.getElementsByTagName("AfterMonth")[0]?.textContent ||
      undefined;
  }

  return {
    id: get("ImportId"),
    dataSource: {
      id: get("MetabaseCardId"),
      name: get("DisplayName"),
      type: (get("DataSourceType") as DataSourceType) || "question",
      collection: get("Collection"),
    },
    status: "imported",
    sheetId: get("SheetId") || undefined,
    sheetName: get("SheetName") || undefined,
    tableName: get("TableName") || undefined,
    xmlPartId: xmlPartId,
    lastRefreshed: get("LastRefreshed") || undefined,
    rowCount: parseInt(get("RowCount"), 10) || undefined,
    contentHash: get("ContentHash") || undefined,
    columnSchema,
    dateColumn,
    dateColumnDisplayName,
    datePreset,
    afterMonth,
  };
}

function escapeXml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}
